import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import NavBar from "../components/navbar";
import Footer from "../components/footer";
import Reviews from "../components/Reviews";
import {
  getDetails,
  getCredits,
  getVideos,
  getTrailerKey,
  posterUrl,
  backdropUrl,
  imgUrl,
} from "../services/tmdb";

function shareLink() {
  const url = window.location.href;
  const title = document.title;

  if (navigator.share) {
    navigator.share({ title, url })
      .then(() => console.log("Successfully shared"))
      .catch((error) => console.error("Error sharing", error));
  } else {
    navigator.clipboard
      .writeText(url)
      .then(() => alert("Link copied to clipboard!"))
      .catch((err) => console.error("Could not copy text: ", err));
  }
}

function Details() {
  const { mediaType, id } = useParams();
  const [details, setDetails] = useState(null);
  const [cast, setCast] = useState([]);
  const [trailerKey, setTrailerKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    async function fetchAll() {
      try {
        const [det, cred, vids] = await Promise.all([
          getDetails(mediaType, id),
          getCredits(mediaType, id),
          getVideos(mediaType, id),
        ]);

        if (!det || det.success === false) {
          setError("Title not found.");
        } else {
          setDetails(det);
          setCast(cred?.cast?.slice(0, 12) || []);
          setTrailerKey(getTrailerKey(vids));
        }
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    fetchAll();
  }, [mediaType, id]);

  if (loading)
    return (
      <div>
        <NavBar />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
          <div className="text-center">
            <div className="spinner-border text-warning mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-white-50">Loading details...</p>
          </div>
        </div>
      </div>
    );
    
  if (error)
    return (
      <div>
        <NavBar />
        <div className="text-white text-center py-5" style={{ minHeight: "60vh" }}>
          <h2 className="mb-3">😕 Oops!</h2>
          <p className="text-white-50 fs-5">{error}</p>
        </div>
      </div>
    );

  const title = details.title || details.name || "Unknown";
  const releaseDate = details.release_date || details.first_air_date || "";
  const year = releaseDate.substring(0, 4);
  const runtime = details.runtime
    ? `${Math.floor(details.runtime / 60)}h ${details.runtime % 60}m`
    : details.episode_run_time?.[0]
    ? `${details.episode_run_time[0]} min/ep`
    : "";
  const genres = details.genres?.map((g) => g.name).join(", ") || "";
  const poster = posterUrl(details.poster_path);
  const backdrop = backdropUrl(details.backdrop_path);
  const rating = details.vote_average ? details.vote_average.toFixed(1) : "N/A";

  return (
    <div>
      <NavBar />

      {/* Backdrop hero */}
      {backdrop && (
        <div
          className="position-relative"
          style={{
            height: "250px",
            overflow: "hidden",
            marginBottom: "-60px",
          }}
        >
          <img
            src={backdrop}
            alt={title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.4)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "200px",
              background: "linear-gradient(to top, var(--bg-dark) 0%, transparent 100%)",
            }}
          />
        </div>
      )}

      <div className="container position-relative" style={{ zIndex: 2 }}>
        <div className="row">
          <div className="col-4 col-sm-4 col-md-3 col-lg-3 mb-3 mb-md-0">
            {poster ? (
              <img
                className="rounded shadow-lg w-100"
                src={poster}
                alt={title}
                style={{ marginTop: backdrop ? "-80px" : "0", border: "3px solid rgba(255,255,255,0.1)", maxWidth: "280px" }}
              />
            ) : (
              <div className="skeleton-loader rounded" style={{ aspectRatio: "2/3", marginTop: backdrop ? "-80px" : "0" }} />
            )}
          </div>
          <div className="col-8 col-sm-8 col-md-9 col-lg-9">
            <div className="d-flex justify-content-between align-items-start">
              <div style={{ minWidth: 0 }}>
                <h1 className="text-white fw-bold mb-1 details-title">{title}</h1>
                <p className="text-white-50 mb-2 small details-meta">
                  {year}{runtime && ` • ${runtime}`}{genres && ` • ${genres}`}
                </p>
              </div>
              <svg
                onMouseOver={() => setHover(true)}
                onMouseOut={() => setHover(false)}
                onClick={shareLink}
                className="p-2 border border-1 rounded flex-shrink-0 ms-2 mt-1"
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                fill="#ffffff"
                viewBox="0 0 16 16"
                style={{ cursor: hover ? "pointer" : "default", transition: "0.2s" }}
              >
                <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5" />
              </svg>
            </div>

            <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: "8px" }}>
              <span
                className="text-black fw-bold px-2 py-1 rounded"
                style={{ backgroundColor: "#e7b816", fontSize: "0.9rem" }}
              >
                ⭐ {rating}
              </span>
              <span className="text-white-50 small">
                {details.vote_count?.toLocaleString()} votes
              </span>
              {details.status && (
                <span className="badge rounded-pill" style={{ background: "rgba(255,255,255,0.1)", color: "var(--text-secondary)", fontSize: "0.75rem" }}>
                  {details.status}
                </span>
              )}
            </div>

            <p className="text-white d-none d-md-block" style={{ lineHeight: "1.8", fontSize: "1.05rem" }}>{details.overview}</p>

            <div className="d-none d-md-block">
              {details.created_by?.length > 0 && (
                <p className="text-white-50 mb-1"><strong className="text-white">Created by:</strong> {details.created_by.map(c => c.name).join(", ")}</p>
              )}
              {details.number_of_seasons && (
                <p className="text-white-50 mb-1"><strong className="text-white">Seasons:</strong> {details.number_of_seasons} ({details.number_of_episodes} episodes)</p>
              )}
              {details.production_companies?.length > 0 && (
                <p className="text-white-50 mb-1"><strong className="text-white">Studio:</strong> {details.production_companies.slice(0, 3).map(c => c.name).join(", ")}</p>
              )}
            </div>
          </div>
        </div>

        {/* Mobile-only overview */}
        <div className="d-md-none mt-3">
          <p className="text-white" style={{ lineHeight: "1.8" }}>{details.overview}</p>
        </div>

        {/* Trailer */}
        <div className="row mt-5">
          <div className="col-12">
            <h4 className="text-white mb-3 fw-bold">🎬 Trailer</h4>
            <div className="rounded" style={{ height: "450px", maxHeight: "60vh" }}>
              {trailerKey ? (
                <iframe
                  className="rounded w-100 h-100"
                  src={`https://www.youtube.com/embed/${trailerKey}`}
                  title="Trailer"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                ></iframe>
              ) : (
                <div
                  className="rounded d-flex flex-column align-items-center justify-content-center h-100"
                  style={{
                    background: "linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                    border: "1px solid rgba(255,255,255,0.08)"
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="rgba(255,255,255,0.15)" viewBox="0 0 16 16">
                    <path d="M6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814z"/>
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 0a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1z"/>
                  </svg>
                  <span className="text-white-50 mt-3 small">No trailer available</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cast */}
        {cast.length > 0 && (
          <div className="row mt-5">
            <div className="col-12">
              <h4 className="text-white mb-3 fw-bold">🎭 Cast</h4>
            </div>
            <div className="d-flex flex-wrap" style={{ gap: "16px" }}>
              {cast.map((actor) => (
                <div
                  key={actor.id}
                  className="text-center"
                  style={{ width: "100px", flexShrink: 0 }}
                >
                  {actor.profile_path ? (
                    <img
                      src={imgUrl(actor.profile_path, "w185")}
                      alt={actor.name}
                      className="rounded-circle shadow-sm mb-2"
                      style={{ width: "72px", height: "72px", objectFit: "cover", border: "2px solid rgba(255,255,255,0.1)" }}
                    />
                  ) : (
                    <div
                      className="rounded-circle shadow-sm mb-2 d-flex align-items-center justify-content-center mx-auto"
                      style={{ width: "72px", height: "72px", background: "rgba(255,255,255,0.08)" }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="rgba(255,255,255,0.3)" viewBox="0 0 16 16">
                        <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z"/>
                      </svg>
                    </div>
                  )}
                  <p className="text-white small mb-0 fw-bold" style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>{actor.name}</p>
                  <p className="text-white-50" style={{ fontSize: "0.65rem" }}>{actor.character}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews */}
        <div className="row mt-5">
          <div className="col-12">
            <Reviews imdbID={`tmdb_${mediaType}_${id}`} />
          </div>
        </div>
      </div>
      <hr style={{ color: "white" }} className="mt-5"></hr>
      <Footer />
    </div>
  );
}

export default Details;
